import { concat } from '@ember/helper';
import { or } from 'tio-ui/utilities';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import DownloadButton from 'tio-ui/components/download-button';
import DefinitionList from 'tio-common/components/ui/definition-list';
import Modal from 'tio-ui/components/modal';
import Button from 'tio-ui/components/button';
import Divider from 'tio-ui/components/divider';
import { VStack } from 'tio-ui/components/layout';
import { PhEye, PhLink, PhArrowRight, PhUserCheck } from 'ember-phosphor-icons';
import Chip from 'tio-ui/components/chip';
import Popover from 'tio-ui/components/popover';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';
import type { PslfForm } from 'tio-data/data/resources/search.ext';
import type IntlService from 'ember-intl/services/intl';
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type { NotificationsService } from 'tio-ui/components/notifications';
import { findRecord } from '@warp-drive/utilities/json-api';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import ENV from 'tio-employee/config/environment';

interface PslfAdminCompanyFormsTableRowSig {
  Args: {
    model: PslfForm;
    rowClass: string;
    cellClass: string;
  };
  Element: HTMLTableRowElement;
}

export default class PslfAdminCompanyFormsTableRowComponent extends Component<PslfAdminCompanyFormsTableRowSig> {
  @service declare intl: IntlService;
  @service declare store: StoreService;

  // Provides request auth headers to DownloadButton; keeps that UI component free of any store/adapter tie.
  // WarpDrive Phase 2 — replaces adapter.headers read; auth-context is owner-free (ADR-001).
  downloadHeaders = (): Record<string, string> =>
    Object.fromEntries(buildTioAuthHeaders({ apiKey: ENV.apiKey }).entries());
  @service declare notifications: NotificationsService;
  @service declare sessionContext: SessionContextService;

  @tracked showDetailsModal = false;
  @tracked formDetails?: PslfFormModel;
  @tracked isLoadingDetails = false;

  get isPriorEmployerForm() {
    return this.args.model?.formType === 'priorEmployer';
  }

  get isNonPartnerApproverForm() {
    return !!this.args.model?.formNonPartner;
  }

  get formTypeDisplayKey() {
    if (this.isPriorEmployerForm && !this.isNonPartnerApproverForm) {
      return 'currentEmployerPriorPeriod';
    }
    return this.args.model.formType;
  }

  get needsReview() {
    return this.args.model?.formStatus === 'borrower_signed' && !this.isNonPartnerApproverForm;
  }

  get isOboForm() {
    return !!this.args.model?.pslfFormIsSubmittedOboBoolean;
  }

  get isOboByCurrentEmployee() {
    return (
      this.args.model?.pslfFormSubmittedOboByEmployeeIdKeyword ===
      this.sessionContext.currentEmployee?.id
    );
  }

  get approverDisplayEmail() {
    const model = this.args.model;

    if (model.formPrintAndMail) {
      return `${this.intl.t('pslf.table.not_applicable')} - ${this.intl.t(
        'pslf.table.print_and_mail'
      )}`;
    }
    if (model.employerClosed) {
      return `${this.intl.t('pslf.table.not_applicable')} - ${this.intl.t(
        'pslf.table.prior_employer_closed'
      )}`;
    }
    return model.employerEmail || '';
  }

  get normalizedEmployerSignDate() {
    const date = this.args.model.employerSignDate;
    if (!date) return '';
    // old forms include the timestamp
    if (date.includes('T')) {
      return date.split('T')[0]!;
    }
    return date;
  }

  get normalizedBorrowerSignDate() {
    const date = this.args.model.borrowerSignDate;
    if (!date) return '';
    // old forms include the timestamp
    if (date.includes('T')) {
      return date.split('T')[0]!;
    }
    return date;
  }

  needsUrl = async () => {
    // WarpDrive builder read (Phase 2.4) — replaces store.findRecord.
    const { content } = (await this.store.request(
      findRecord<PslfFormModel>('pslf-form', this.args.model.formId, {
        include: 'attachments',
      })
    )) as { content: { data: PslfFormModel } };
    const form = content.data;

    const attachment = form.attachments?.find((attachment) => {
      return attachment.tags.includes(`PslfForm:${this.args.model.formId}`);
    });

    return attachment?.signedUrl ?? '';
  };

  openDetailsModal = async () => {
    this.showDetailsModal = true;
    this.isLoadingDetails = true;
    try {
      // WarpDrive builder read (Phase 2.4) — replaces store.findRecord.
      const { content } = (await this.store.request(
        findRecord<PslfFormModel>('pslf-form', this.args.model.formId, {
          include: 'attachments',
        })
      )) as { content: { data: PslfFormModel } };
      this.formDetails = content.data;
    } catch (e) {
      console.error('Failed to load form details', e);
    } finally {
      this.isLoadingDetails = false;
    }
  };

  closeDetailsModal = () => {
    this.showDetailsModal = false;
    this.formDetails = undefined;
  };

  get formFein() {
    return this.formDetails?.employerFields?.['employer-fein'] || '';
  }

  get employmentStartDate() {
    return this.formDetails?.employerFields?.['employment-start-date'] || '';
  }

  get employmentEndDate() {
    if (this.formDetails?.employerFields?.['employment-continues']) {
      return this.intl.t('pslf.table.present');
    }
    return this.formDetails?.employerFields?.['employment-end-date'] || '';
  }

  get employmentHours() {
    return this.formDetails?.employerFields?.['employment-hours'] || '';
  }

  copySignLink = async () => {
    try {
      await navigator.clipboard.writeText(this.formDetails?.employerSignLink || '');
      this.notifications.add(this.intl.t('pslf.notifications.copy_success'), {
        appearance: 'success',
      });
    } catch (e) {
      this.notifications.add(this.intl.t('pslf.notifications.copy_error'), {
        appearance: 'error',
      });
      console.error(e);
    }
  };

  <template>
    <tr class={{@rowClass}} ...attributes>
      <td class={{@cellClass}}>{{@model.payrollId}}</td>
      <td class={{@cellClass}}>{{@model.borrowerName}}</td>
      {{#if (eq @model.formStatus "borrower_signed")}}
        <td class={{@cellClass}}>
          {{#if this.needsReview}}
            <LinkTo
              data-test-form-review-link
              @route="authenticated.admin.pslf.form.review"
              @model={{@model.formId}}
              class="tio-fg-danger items-center font-semibold truncate underline"
            >
              {{t "common.pslf.status.needs_review"}}
            </LinkTo>
          {{else}}
            {{t (concat "common.pslf.status." @model.formStatus)}}
          {{/if}}
        </td>
      {{else}}
        <td class={{@cellClass}}>{{t (concat "common.pslf.status." @model.formStatus)}}</td>
      {{/if}}
      <td class={{@cellClass}}>{{this.approverDisplayEmail}}</td>
      {{#let this.normalizedBorrowerSignDate as |date|}}
        <td class={{@cellClass}} title={{date}}>
          {{#if this.isOboForm}}
            <div class="tio-col-extra-tight">
              <Popover @triggerMode="hover">
                <:trigger as |pop|>
                  <Chip
                    @intent="primary"
                    @size="sm"
                    {{pop.register}}
                    {{pop.hoverHandlers}}
                    tabindex="0"
                    class="cursor-help w-fit"
                  >
                    <PhUserCheck @size="12" />
                    {{#if this.isOboByCurrentEmployee}}
                      {{t "pslf.admin_forms.submitted_by_you"}}
                    {{else}}
                      {{t "common.pslf.forms.submitted_by_approver"}}
                    {{/if}}
                  </Chip>
                </:trigger>
                <:body>
                  <div class="p-3 max-w-xs">
                    {{#if this.isOboByCurrentEmployee}}
                      {{t "pslf.admin_forms.submitted_by_you_tooltip"}}
                    {{else}}
                      {{t "pslf.admin_forms.submitted_by_approver_tooltip"}}
                    {{/if}}
                  </div>
                </:body>
              </Popover>
              {{#if date}}
                <span class="text-sm tio-fg-muted">
                  {{safeFormatDate date month="short" day="2-digit" year="numeric" timeZone="UTC"}}
                </span>
              {{/if}}
            </div>
          {{else if date}}
            {{safeFormatDate date month="short" day="2-digit" year="numeric" timeZone="UTC"}}
          {{else}}
            {{t "pslf.table.pending"}}
          {{/if}}
        </td>
      {{/let}}
      {{#let this.normalizedEmployerSignDate as |date|}}
        <td class={{@cellClass}} title={{date}}>
          {{#if date}}
            {{safeFormatDate date month="short" day="2-digit" year="numeric" timeZone="UTC"}}
          {{else if (or @model.formPrintAndMail @model.employerClosed)}}
            {{t "pslf.table.not_applicable"}}
          {{else if @model.employerSignDate}}
            {{t "pslf.table.pending"}}
          {{/if}}
        </td>
      {{/let}}
      <td class={{@cellClass}}>
        <div>
          {{t (concat "common.pslf.form-type." this.formTypeDisplayKey)}}
          {{#if this.isPriorEmployerForm}}
            <div class="text-sm tio-fg-secondary">
              {{@model.employerName}}
            </div>
          {{/if}}
        </div>
      </td>
      <td class="{{@cellClass}} whitespace-nowrap">
        <Button
          data-test-view-details-button
          @intent="primary"
          @appearance="minimal"
          @icon={{PhEye}}
          title={{t "pslf.table.view_details"}}
          {{on "click" this.openDetailsModal}}
        />
      </td>
    </tr>

    <Modal @isOpen={{this.showDetailsModal}} @onClose={{this.closeDetailsModal}} @size="lg">
      <:header>{{t "pslf.table.form_details"}}</:header>
      <:body>
        {{#if this.isLoadingDetails}}
          <p class="tio-fg-muted">{{t "common.reporting.loading"}}</p>
        {{else if this.formDetails}}
          <VStack>
            <DefinitionList as |options|>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "pslf.table.form_id"}}</options.dt>
                <options.dd>{{@model.formId}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.form_type"}}</options.dt>
                <options.dd>{{t
                    (concat "common.pslf.form-type." this.formTypeDisplayKey)
                  }}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "pslf.table.status"}}</options.dt>
                <options.dd>
                  {{#if this.needsReview}}
                    <LinkTo
                      @route="authenticated.admin.pslf.form.review"
                      @model={{@model.formId}}
                      class="tio-inline-row gap-1 tio-fg-danger font-semibold underline hover:tio-fg-danger-body"
                      data-test-modal-review-link
                    >
                      {{t "common.pslf.status.needs_review"}}
                      <PhArrowRight @size="14" />
                    </LinkTo>
                  {{else}}
                    {{t (concat "common.pslf.status." @model.formStatus)}}
                  {{/if}}
                </options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.employer_name"}}</options.dt>
                <options.dd>{{or this.formDetails.employerDisplayName "—"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.fein"}}</options.dt>
                <options.dd>{{or this.formFein "—"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.employment_dates"}}</options.dt>
                <options.dd>
                  {{#if this.employmentStartDate}}
                    {{safeFormatDate
                      this.employmentStartDate
                      month="short"
                      day="2-digit"
                      year="numeric"
                      timeZone="UTC"
                    }}
                    —
                    {{#if (eq this.employmentEndDate (t "pslf.table.present"))}}
                      {{t "pslf.table.present"}}
                    {{else if this.employmentEndDate}}
                      {{safeFormatDate
                        this.employmentEndDate
                        month="short"
                        day="2-digit"
                        year="numeric"
                        timeZone="UTC"
                      }}
                    {{else}}
                      —
                    {{/if}}
                  {{else}}
                    —
                  {{/if}}
                </options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.weekly_hours"}}</options.dt>
                <options.dd>{{or this.employmentHours "—"}}</options.dd>
              </div>
              {{#if this.normalizedBorrowerSignDate}}
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "pslf.table.borrower_signed_date"}}</options.dt>
                  <options.dd>
                    {{safeFormatDate
                      this.normalizedBorrowerSignDate
                      month="short"
                      day="2-digit"
                      year="numeric"
                      timeZone="UTC"
                    }}
                  </options.dd>
                </div>
              {{/if}}
              {{#if this.normalizedEmployerSignDate}}
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "pslf.table.employer_signed_date"}}</options.dt>
                  <options.dd>
                    {{safeFormatDate
                      this.normalizedEmployerSignDate
                      month="short"
                      day="2-digit"
                      year="numeric"
                      timeZone="UTC"
                    }}
                  </options.dd>
                </div>
              {{/if}}
            </DefinitionList>

            <Divider class="my-4" />

            <div class="tio-row-loose">
              {{#if (or @model.borrowerSignDate @model.employerSignDate)}}
                <DownloadButton
                  @authHeaders={{this.downloadHeaders}}
                  @needsUrl={{this.needsUrl}}
                  @filename={{concat @model.borrowerName " - " @model.formId}}
                />
              {{/if}}

              {{#if this.formDetails.isNonPartner}}
                {{#if this.formDetails.employerSignLink}}
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @icon={{PhLink}}
                    title={{t "pslf.table.copy_sign_link"}}
                    class="tio-inline-row gap-1"
                    @label={{t "pslf.table.copy_sign_link"}}
                    {{on "click" this.copySignLink}}
                  />
                {{/if}}
              {{/if}}
            </div>
          </VStack>
        {{/if}}
      </:body>
    </Modal>
  </template>
}
